import { defineRoutes } from '../utils';

export type OrderNavParms = {
  /** the id of the order */
  id?: string;
};

const BASE = '/dashboard/order';
const LOCATION = `${BASE}/location`;

/**
 * Order domain routes — covers create-order surfaces, the "my orders"
 * list, the carrier "new loads" board, and the legacy customer-dashboard
 * order wizard / location wizard steps.
 *
 * Naming
 * - `new_order` is the canonical "create order" entry, used by the
 *   carrier-dashboard's single-page `<PublishOrderPage />` wrapper.
 * - `order_*` (order_new, order_date, order_create, order_published,
 *   order_location_*) are the customer-dashboard's multi-step wizard
 *   steps; they are kept untouched by the carrier-side migration.
 * - `orders` is the canonical "my orders" list. Both apps now mount it
 *   at `/my-orders`; the customer-dashboard keeps `/loads` as a legacy
 *   redirect for bookmarked URLs.
 * - `new_loads` is the carrier-only "published orders" / jobs board.
 */
export const orderRoutes = defineRoutes({
  /**
   * Carrier-dashboard "create order" entry. Mounts the shared
   * `<PublishOrderPage />` from `@hero/web-components` at a
   * top-level `/dashboard/new-order` so the URL reads as the action.
   *
   * Optional `clientId` search param — when present, the page hydrates
   * the publish-store's `customer` slot from the matching CRM Client
   * doc on mount (see `PublishOrderPage`). Used by the CRM Orders tab
   * "New order" CTA so the carrier lands on a draft already scoped to
   * the client they were viewing.
   */
  new_order: {
    path: '/dashboard/new-order',
    isPublic: false,
    params: {} as { clientId?: string },
  },
  /** Carrier-only published orders board. */
  new_loads: {
    path: '/new-loads',
    isPublic: false,
  },
  /** Customer-dashboard wizard step 1 — pickup / dropoff entry. */
  order_new: {
    path: `${BASE}/new`,
    isPublic: false,
  },
  /** Customer-dashboard wizard step 2 — pick pickup date. */
  order_date: {
    path: `${BASE}/date`,
    isPublic: false,
  },
  /**
   * Customer-dashboard wizard step 3 — mounts `<PublishOrderPage />`.
   * Same shared component as `new_order` but reached through the
   * customer wizard. Left untouched by the carrier migration.
   */
  order_create: {
    path: `${BASE}/create`,
    isPublic: false,
  },
  order_published: {
    path: `${BASE}/published`,
    isPublic: false,
  },
  /**
   * Canonical "my orders" list, shared by both dashboards at
   * `/my-orders`. The customer-dashboard keeps `/loads` as a legacy
   * redirect; carrier-dashboard already lives at `/my-orders`.
   */
  orders: {
    path: '/my-orders',
    isPublic: false,
    params: {} as OrderNavParms,
  },
  /**
   * PREVIEW / DESIGN-REVIEW surface for the financing-style
   * expandable orders layout. **Do not** add a sidebar link, **do
   * not** wire it to live data, and **do not** delete this entry —
   * the product owner has asked to keep it parked as a reference
   * until they explicitly approve promotion to the real
   * `/my-orders` page.
   *
   * Reachable ONLY by typing `/my-orders/preview` into the address
   * bar on a logged-in carrier-dashboard session. The page lives at
   * `apps/carrier-dashboard/src/pages/MyOrders/preview/` and is
   * mock-only (no API, no translations, hardcoded English).
   *
   * Promotion path + rules documented in
   * `apps/carrier-dashboard/src/pages/MyOrders/preview/README.md`.
   */
  orders_preview: {
    path: '/my-orders/preview',
    isPublic: false,
  },
  order_location_add: {
    path: `${LOCATION}/add`,
    isPublic: false,
  },
  order_location_edit: {
    path: `${LOCATION}/edit/:id`,
    isPublic: false,
    params: {} as { id: string },
  },
  order_location_customer: {
    path: `${LOCATION}/customer`,
    isPublic: false,
  },
  order_location_customer_edit: {
    path: `${LOCATION}/customer/:id`,
    isPublic: false,
    params: {} as { id: string },
  },
  order_location_contact_create: {
    path: `${LOCATION}/contact/create`,
    isPublic: false,
  },
});
